import { useEffect, useState } from "react";
import { ChevronLeft, Info } from "lucide-react";
import ToggleSwitch from "./ToggleSwitch";
import GroupingConfirmModal from "./GroupingConfirmModal";
import MenuIconTooltip from "./MenuIconTooltip";
import { API_URL } from "../config/api";
import { formatGameResponseDuration } from "../constants/potions";
import { buildEwssEvolutionGrades } from "../utils/ewssRetention";
import { reportQuestWordCounts } from "../utils/questProgressReporter";
import { pickTitleWordCounts } from "../utils/titleWordCounts";
import "./RegisterSkeleton.css";

const DETAILS_TABLE_CELL_STYLE = {
  padding: "6px",
  color: "#39210d",
};

const INSUFFICIENT_DATA_TOOLTIP =
  "Un mot doit être joué au moins 3 fois pour que sa mémorisation soit évaluée.";

const EWSS_SCORE_TOOLTIP = (
  <>
    <span className="vocab-tooltip-line" style={{ display: "block" }}>
      EWSS : Exponentially Weighted Success Score.
    </span>
    <span className="vocab-tooltip-line" style={{ display: "block", marginTop: 4 }}>
      Moyenne des 5 dernières parties, pondérée exponentiellement.
    </span>
    <span className="vocab-tooltip-line" style={{ display: "block", marginTop: 4 }}>
      Le récent pèse plus, comme la mémoire humaine.
    </span>
    <span className="vocab-tooltip-line" style={{ display: "block", marginTop: 4 }}>
      Une réussite récente valorise ; un échec récent pénalise davantage.
    </span>
  </>
);

function WordDetailInfoTip({ tooltip, ariaLabel }) {
  return (
    <MenuIconTooltip
      tooltip={tooltip}
      placement="above"
      portal
      className="word-detail-info-tip"
    >
      <button
        type="button"
        className="vocab-info-button"
        aria-label={ariaLabel}
      >
        <Info size={13} strokeWidth={2} aria-hidden="true" />
      </button>
    </MenuIconTooltip>
  );
}

function WordDetailsSkeleton() {
  return (
    <div
      className="word-details-skeleton"
      aria-hidden="true"
      style={{
        padding: "20px",
        color: "#39210d",
        fontFamily: "Georgia, serif",
        lineHeight: "1",
      }}
    >
      <div className="word-detail-ornament">
        <span className="word-details-skeleton__ornament-letter">A</span>
      </div>
      <h2
        className="word-detail-title"
        style={{ textAlign: "center", margin: "0px", fontSize: "calc(26px * var(--text-scale, 1))", color: "#39210d" }}
      >
        <span className="word-detail-title__initial">A</span>
        <span className="register-skel register-skel--title" />
      </h2>

      <div
        style={{
          lineHeight: "0",
          marginTop: "25px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <p>
          <span className="word-details-skeleton__text-metrics">
            Ce mot a été capturé le 00/00/0000
          </span>
          <span className="register-skel register-skel--line register-skel--line-lg" />
        </p>
        <p>
          <span className="word-details-skeleton__text-metrics">
            Dans Titre de l&apos;ouvrage de Auteur
          </span>
          <span className="register-skel register-skel--line register-skel--line-xl" />
        </p>
      </div>

      <h3
        style={{
          marginTop: "30px",
          marginLeft: "18px",
          marginBottom: "10px",
          fontWeight: "normal",
          fontSize: "calc(16px * var(--text-scale, 1))",
        }}
      >
        <span className="word-details-skeleton__text-metrics">
          Mémorisation: maîtrisé
        </span>
        <span className="register-skel register-skel--line register-skel--line-md" />
      </h3>

      <div className="containerStat">
        <div className="cs-col1">
          <ul>
            <li>
              <span className="register-skel register-skel--stat-line" style={{ width: "96px" }} />
            </li>
            <li>
              <span className="register-skel register-skel--stat-line" style={{ width: "128px" }} />
            </li>
            <li>
              <span className="register-skel register-skel--stat-line" style={{ width: "108px" }} />
            </li>
            <li>
              <span className="register-skel register-skel--stat-line" style={{ width: "138px" }} />
            </li>
          </ul>
        </div>

        <div className="cs-col2">
          <img
            className="word-details-skeleton__laurel"
            src="/761364_ornaments.svg"
            alt=""
          />
        </div>

        <div className="cs-col3">
          <div className="word-details-skeleton__toggle">
            <span className="register-skel register-skel--toggle-label" style={{ width: "78px" }} />
            <span className="register-skel register-skel--toggle-switch" />
          </div>
          <div className="word-details-skeleton__toggle">
            <span className="register-skel register-skel--toggle-label" style={{ width: "108px" }} />
            <span className="register-skel register-skel--toggle-switch" />
          </div>
          <div className="word-details-skeleton__toggle">
            <span className="register-skel register-skel--toggle-label" style={{ width: "92px" }} />
            <span className="register-skel register-skel--toggle-switch" />
          </div>
        </div>
      </div>

      <div
        data-scroll-anchor="stats-table"
        style={{
          width: "100%",
          marginTop: "20px",
          backdropFilter: "blur(10px)",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "calc(14px * var(--text-scale, 1))",
            fontWeight: 400,
          }}
        >
          <thead>
            <tr
              style={{
                background: "rgba(57, 33, 13, 0.05)",
                borderBottom: "1px solid rgba(57, 33, 13, 0.1)",
              }}
            >
              <th style={{ padding: "6px", textAlign: "left", fontWeight: 400 }}>
                <span className="register-skel register-skel--cell" style={{ width: "36px" }} />
              </th>
              <th style={{ padding: "6px", textAlign: "center", fontWeight: 400 }}>
                <span
                  className="register-skel register-skel--cell"
                  style={{ width: "54px", margin: "0 auto" }}
                />
              </th>
              <th style={{ padding: "6px", textAlign: "center", fontWeight: 400, width: "52px" }}>
                <span
                  className="register-skel register-skel--cell"
                  style={{ width: "38px", margin: "0 auto" }}
                />
              </th>
              <th style={{ padding: "6px", textAlign: "center", fontWeight: 400, width: "56px" }}>
                <span
                  className="register-skel register-skel--cell"
                  style={{ width: "36px", margin: "0 auto" }}
                />
              </th>
              <th style={{ padding: "6px", textAlign: "center", fontWeight: 400 }}>
                <span
                  className="register-skel register-skel--cell"
                  style={{ width: "58px", margin: "0 auto" }}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {[72, 58, 64].map((width, index) => (
              <tr
                key={index}
                style={{
                  borderBottom:
                    index < 2 ? "1px solid rgba(57, 33, 13, 0.08)" : "none",
                }}
              >
                <td style={DETAILS_TABLE_CELL_STYLE}>
                  <span className="register-skel register-skel--cell" style={{ width: "103px" }} />
                </td>
                <td style={{ ...DETAILS_TABLE_CELL_STYLE, textAlign: "center" }}>
                  <span className="register-skel register-skel--result" />
                </td>
                <td style={{ ...DETAILS_TABLE_CELL_STYLE, textAlign: "center" }}>
                  <span
                    className="register-skel register-skel--cell"
                    style={{ width: "18px", margin: "0 auto" }}
                  />
                </td>
                <td style={{ ...DETAILS_TABLE_CELL_STYLE, textAlign: "center" }}>
                  <span
                    className="register-skel register-skel--cell"
                    style={{ width: "28px", margin: "0 auto" }}
                  />
                </td>
                <td style={{ ...DETAILS_TABLE_CELL_STYLE, textAlign: "center" }}>
                  <span
                    className="register-skel register-skel--cell"
                    style={{ width: `${width}px`, margin: "0 auto" }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
// Composant CircleAlert
function CircleAlert({ className = "", style = {} }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" x2="12" y1="8" y2="12" />
      <line x1="12" x2="12.01" y1="16" y2="16" />
    </svg>
  );
}

// Composant CircleCheck
function CircleCheck({ className = "", style = {} }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
    >
      <path d="M21.801 10A10 10 0 1 1 17 3.335" />
      <path d="m9 11 3 3L22 4" />
    </svg>
  );
}

function renderGradeInLaurel(grade) {
  const match = /^([A-F])([+-])$/.exec(grade);
  if (!match) return grade;

  return (
    <>
      <span className="containerGrade__letter">{match[1]}</span>
      <sup className="containerGrade__modifier">{match[2]}</sup>
    </>
  );
}

export default function WordDetailsPanel({ mot, onListRefresh, forceSkeleton = false }) {
  const [data, setData] = useState(null);
  const [isFavorite, setFavorite] = useState(false);
  const [isPriority, setPriority] = useState(false);
  const [resetStats, setResetStats] = useState(false);
  const [resetStatsConfirmOpen, setResetStatsConfirmOpen] = useState(false);

  useEffect(() => {
    if (!mot) return;
    let cancelled = false;

    const fetchDetails = async () => {
      const url = `${API_URL}/mots/${encodeURIComponent(mot)}`;
      try {
        const res = await fetch(url);
        const json = await res.json();
        if (cancelled) return;
        setData(json);
        setFavorite(json?.categorie?.coeur === 1);
        setPriority(json?.categorie?.top20 === 1);
        setResetStats(false);
        setResetStatsConfirmOpen(false);
      } catch (err) {
        if (!cancelled) console.error("Erreur WordDetailsPanel:", err);
      }
    };

    fetchDetails();
    return () => {
      cancelled = true;
    };
  }, [mot]);

  if (!mot) {
    return <div className="word-details-empty" />;
  }

  if (forceSkeleton || !data || data.mot !== mot) {
    return <WordDetailsSkeleton />;
  }

  // Trier par date décroissante (plus récent en premier)
  const sortedGames = data.stats?.last5Games
    ? [...data.stats.last5Games].sort((a, b) => {
        const parseDate = (dateStr) => {
          if (!dateStr) return new Date(0);
          const [day, month, year] = dateStr.split("/").map(Number);
          return new Date(year, month - 1, day);
        };
        const parseTime = (timeStr) => {
          if (!timeStr) return 0;
          const [hh, mm] = timeStr.split(":").map(Number);
          return (hh || 0) * 60 + (mm || 0);
        };
        const dateDiff = parseDate(b.date) - parseDate(a.date);
        if (dateDiff !== 0) return dateDiff;
        return parseTime(b.time) - parseTime(a.time);
      })
    : [];

  const ewssEvolutionGrades = buildEwssEvolutionGrades(sortedGames);

  const retention = data?.stats?.retention;
  const last5Count = data.stats?.last5Games?.length ?? 0;
  const insufficientData =
    !retention ||
    retention.insufficientData === true ||
    last5Count < 3 ||
    retention.grade == null;

  const grade = insufficientData ? null : retention.grade.toString().trim();
  const retentionStatus = insufficientData
    ? "données insuffisantes"
    : (retention.status ?? "—");
  const retentionScore = insufficientData ? "—" : (retention.score ?? "—");
  const paquetNum = data.paquet && data.paquet >= 1 ? data.paquet : null;

  const motChars = data.mot ? Array.from(data.mot) : [];
  const motInitial = (motChars[0] ?? "").toUpperCase();
  const motRest = motChars.slice(1).join("");
  // Majuscule accentuée : TaylorGothic ne la gère pas → repli sur UnifrakturCook.
  const initialIsAccented = /[\u0300-\u036f]/.test(motInitial.normalize("NFD"));

  // Ornement (police Flowery) : réservé aux mots « encourageant » et « maîtrisé ».
  const ORNAMENT_BY_STATUS = {
    encourageant: "m",
    maîtrisé: "H",
  };
  const ornamentLetter = insufficientData
    ? null
    : ORNAMENT_BY_STATUS[retentionStatus] ?? null;


  const handleFavoriteChange = async (checked) => {
  setFavorite(checked);
  try {
    const res = await fetch(`${API_URL}/mots/${encodeURIComponent(mot)}/favori`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ favori: checked }),
    });
    const result = await res.json().catch(() => ({}));
    if (!res.ok) {
      if (result?.message) alert(result.message);
      setFavorite(!checked);
      return;
    }

    setFavorite(result.favori ?? checked);
    setPriority(result.prioritaire ?? isPriority);
    setData((prev) =>
      prev
        ? {
            ...prev,
            categorie: {
              ...(prev.categorie || {}),
              coeur: checked ? 1 : 0,
              top20: prev.categorie?.top20 ?? 0,
            },
          }
        : prev
    );

    if (onListRefresh) onListRefresh();
  } catch (err) {
    console.error("Erreur maj favori :", err);
    setFavorite(!checked);
  }
};

const handlePriorityChange = async (checked) => {
  setPriority(checked);
  try {
    const res = await fetch(`${API_URL}/mots/${encodeURIComponent(mot)}/prioritaire`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ prioritaire: checked }),
    });
    const result = await res.json().catch(() => ({}));
    if (!res.ok) {
      if (result?.message) alert(result.message);
      setPriority(!checked);
      return;
    }

    setPriority(result.prioritaire ?? checked);
    setFavorite(result.favori ?? isFavorite);
    setData((prev) =>
      prev
        ? {
            ...prev,
            categorie: {
              ...(prev.categorie || {}),
              top20: checked ? 1 : 0,
              coeur: prev.categorie?.coeur ?? 0,
            },
          }
        : prev
    );

    if (onListRefresh) onListRefresh();
  } catch (err) {
    console.error("Erreur maj prioritaire :", err);
    setPriority(!checked);
  }
};

const handleResetStats = (checked) => {
  if (!checked) return;
  setResetStatsConfirmOpen(true);
};

const closeResetStatsConfirm = () => {
  if (resetStats) return;
  setResetStatsConfirmOpen(false);
};

const confirmResetStats = async () => {
  setResetStats(true);
  try {
    const res = await fetch(`${API_URL}/mots/${encodeURIComponent(mot)}/stats`, {
      method: "DELETE",
    });
    const result = await res.json().catch(() => ({}));
    if (!res.ok) {
      alert(result.message || "Erreur lors de la réinitialisation des statistiques.");
      return;
    }

    setData((prev) =>
      prev
        ? {
            ...prev,
            stats: result.stats ?? getEmptyStatsFallback(),
          }
        : prev
    );
    if (onListRefresh) onListRefresh();
    reportQuestWordCounts(pickTitleWordCounts(result));
    setResetStatsConfirmOpen(false);
  } catch (err) {
    console.error("Erreur réinitialisation stats :", err);
    alert("Erreur de connexion au serveur.");
  } finally {
    setResetStats(false);
  }
};

function getEmptyStatsFallback() {
  return {
    gameCount: 0,
    correctCount: 0,
    failCount: 0,
    last5Games: [],
    retention: {
      score: null,
      grade: null,
      status: null,
      insufficientData: true,
    },
  };
}

  return (
    <div
      style={{
        padding: "20px",
        color: "#39210d",
        fontFamily: "Georgia, serif",
        lineHeight: "1",
        zIndex: "99999999999999999999999999999999999"
      }}
    >
      {ornamentLetter && (
        <div className="word-detail-ornament" aria-hidden="true">{ornamentLetter}</div>
      )}
      <h2 className="word-detail-title" style={{ textAlign: "center", margin: "0px", fontSize: "calc(26px * var(--text-scale, 1))", color: "#39210d" }}>
        <span
          className={`word-detail-title__initial${
            initialIsAccented ? " word-detail-title__initial--accented" : ""
          }`}
        >
          {motInitial}
        </span>
        {motRest}
      </h2>

      
      <div style={{ lineHeight: "0", marginTop: "25px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
        <p>Ce mot a été capturé le <strong>{data.date}</strong></p>
        <p>Dans <strong>{data.livre}</strong> de <strong> {data.auteur}</strong></p>
      </div>

      <h3
        className="word-detail-memorization"
        style={{ marginTop: "30px", marginLeft: "18px", marginBottom: "10px", fontWeight: "normal", fontSize: "calc(16px * var(--text-scale, 1))", display: "flex", alignItems: "center", flexWrap: "nowrap" }}
      >
        Mémorisation:{" "}
        {insufficientData ? (
          <>
            <strong>données insuffisantes</strong>
            <WordDetailInfoTip
              tooltip={INSUFFICIENT_DATA_TOOLTIP}
              ariaLabel="À propos : données insuffisantes"
            />
          </>
        ) : (
          <strong>{retentionStatus}</strong>
        )}
      </h3>

      <div className="containerStat">
        <div className="cs-col1">
          <ul>
            <li>Jeux : <strong>{data.stats?.gameCount ?? 0}</strong></li>
            <li>Réussites : <strong>{data.stats?.correctCount ?? 0}</strong></li>
            <li>Échecs : <strong>{data.stats?.failCount ?? 0}</strong></li>
            <li className="word-detail-ewss-line" style={{ display: "flex", alignItems: "center" }}>
              Score EWSS: <strong>{retentionScore}</strong>
              <WordDetailInfoTip
                tooltip={EWSS_SCORE_TOOLTIP}
                ariaLabel="À propos du score EWSS"
              />
            </li>
          </ul>
        </div>


        <div className="cs-col2">
          {!insufficientData && grade && (
            <div className="containerGrade">{renderGradeInLaurel(grade)}</div>
          )}

        <img src="/761364_ornaments.svg" alt="Separateur" />

        </div>
        <div className="cs-col3">

  <ToggleSwitch 
    checked={isFavorite} 
    onChange={handleFavoriteChange}
    label="Mot favori"
  />
  <ToggleSwitch 
    checked={isPriority} 
    onChange={handlePriorityChange}
    label="Mot prioritaire"
  />
  <ToggleSwitch 
    checked={resetStats || resetStatsConfirmOpen} 
    onChange={handleResetStats}
    label="Vider les stats"
  />


        </div>

      </div>

      {sortedGames.length > 0 && (
        <>
          
          <div data-scroll-anchor="stats-table" style={{
            width: "100%",
            marginTop: "20px",
            backdropFilter: "blur(10px)",
            overflow: "hidden",
            boxSizing: "border-box",
          }}>
            <table style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "calc(14px * var(--text-scale, 1))",
              fontWeight: 400,
            }}>
              <thead>
                <tr style={{
                  background: "rgba(57, 33, 13, 0.05)",
                  borderBottom: "1px solid rgba(57, 33, 13, 0.1)"
                }}>
                  <th style={{
                    padding: "6px",
                    textAlign: "left",
                    fontWeight: 400,
                    color: "#39210d"
                  }}>Date</th>
                  <th style={{
                    padding: "6px",
                    textAlign: "center",
                    fontWeight: 400,
                    color: "#39210d"
                  }}>Résultat</th>
                  <th style={{
                    padding: "6px",
                    textAlign: "center",
                    fontWeight: 400,
                    color: "#39210d",
                    width: "52px",
                  }}>Potion</th>
                  <th style={{
                    padding: "6px",
                    textAlign: "center",
                    fontWeight: 400,
                    color: "#39210d",
                    width: "56px",
                  }}>Temps</th>
                  <th style={{
                    padding: "6px",
                    textAlign: "center",
                    fontWeight: 400,
                    color: "#39210d"
                  }}>Réponse</th>
                </tr>
              </thead>
              <tbody>
                {sortedGames.map((game, index) => (
                  <tr
                    key={index}
                    style={{
                      borderBottom: index < sortedGames.length - 1 ? "1px solid rgba(57, 33, 13, 0.08)" : "none",
                      transition: "background 0.2s ease"
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "rgba(57, 33, 13, 0.03)"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                  >
                    <td style={{
                      padding: "6px",
                      color: "#39210d"
                    }}>
                      {game.date || "—"}
                      {game.time ? ` ${game.time}` : ""}
                    </td>
                    <td style={{
                      padding: "6px",
                      textAlign: "center"
                    }}>
                      {game.result === "success" ? (
                        <CircleCheck style={{ color: "#166534", display: "inline-block", verticalAlign: "middle" }} />
                      ) : (
                        <CircleAlert style={{ color: "#991b1b", display: "inline-block", verticalAlign: "middle" }} />
                      )}
                    </td>
                    <td style={{
                      padding: "6px",
                      textAlign: "center",
                      color: "#39210d",
                      fontWeight: 400,
                      opacity: game.potionAid ? 1 : 0.45,
                    }}>
                      {game.potionAid === "M" || game.potionAid === "T" ? game.potionAid : "—"}
                    </td>
                    <td style={{
                      padding: "6px",
                      textAlign: "center",
                      color: "#39210d",
                      fontVariantNumeric: "tabular-nums",
                      opacity: game.responseMs != null ? 1 : 0.45,
                    }}>
                      {formatGameResponseDuration(game.responseMs)}
                    </td>
                    <td style={{
                      padding: "6px",
                      textAlign: "center",
                      color: "#39210d",
                      fontStyle: game.given ? "normal" : "italic",
                      opacity: game.given ? 1 : 0.5
                    }}>
                      {game.given || "(aucune réponse)"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{
            maxWidth: "544px",
            margin: "10px auto 0",
            textAlign: "center",
          }}>
            <p style={{
              margin: "0 0 8px",
              fontSize: "calc(13px * var(--text-scale, 1))",
              fontWeight: "600",
              color: "#39210d",
            }}>
              Évolution gradation EWSS
            </p>
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexWrap: "nowrap",
              gap: "4px",
              fontSize: "calc(14px * var(--text-scale, 1))",
              color: "#39210d",
              overflow: "hidden",
            }}>
              {ewssEvolutionGrades.map((grade, index) => (
                <span
                  key={index}
                  style={{ display: "inline-flex", alignItems: "center", gap: "4px", flexShrink: 0 }}
                >
                  {index > 0 && (
                    <ChevronLeft
                      size={14}
                      strokeWidth={2.25}
                      aria-hidden="true"
                      style={{ opacity: 0.5, flexShrink: 0 }}
                    />
                  )}
                  <span style={{
                    fontWeight: 400,
                    whiteSpace: "nowrap",
                    opacity: grade === "—" ? 0.4 : 1,
                  }}>
                    {grade}
                  </span>
                </span>
              ))}
            </div>
          </div>
        </>
      )}

      <GroupingConfirmModal
        open={resetStatsConfirmOpen}
        title="Vider les statistiques"
        confirmLabel="Valider"
        cancelLabel="Annuler"
        danger
        busy={resetStats}
        onConfirm={confirmResetStats}
        onClose={closeResetStatsConfirm}
      >
        <p>
          Les parties jouées, les réussites, les échecs et le score EWSS de ce
          mot seront remis à zéro. Cette action est irréversible.
        </p>
      </GroupingConfirmModal>
    </div>
  );
}